import { Fragment, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { HandCoins, Euro, Eye, ChevronDown, ChevronUp, Trash2, CheckCircle2, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v || 0);
const formatDate = (d) => {
  if (!d) return '-';
  const dt = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T12:00:00`) : new Date(d);
  return dt.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
};
const todayISO = () => new Date().toISOString().slice(0, 10);

function PaymentDialog({ bike, onClose, onSaved }) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('');
  const [note, setNote] = useState('');
  const [date, setDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);

  if (!bike) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${API}/bicycles/${bike.id}/payments`, {
        amount: parseFloat(amount), method: method || null, note: note || null, date: date || null
      });
      toast.success(res.data.payment_status === 'cobrada' ? 'Venta cobrada completamente' : 'Pago registrado');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al registrar el pago');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!bike} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-['Barlow_Condensed']">Registrar cobro nº {(bike.payments?.length || 0) + 1} · Bici #{bike.number}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="bg-slate-50 p-3 rounded-lg text-sm grid grid-cols-3 gap-2">
            <div><p className="text-xs text-slate-500 uppercase">Precio venta</p><p className="font-semibold">{formatCurrency(bike.sold_price)}</p></div>
            <div><p className="text-xs text-slate-500 uppercase">Cobrado</p><p className="font-semibold text-green-700">{formatCurrency(bike.amount_paid)}</p></div>
            <div><p className="text-xs text-slate-500 uppercase">Pendiente</p><p className="font-semibold text-orange-700">{formatCurrency(bike.amount_pending)}</p></div>
          </div>
          <div className="space-y-2">
            <Label>Importe a cobrar</Label>
            <div className="relative">
              <Euro className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input type="number" step="0.01" min="0.01" max={bike.amount_pending} className="pl-9" required
                value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payment-amount-input" />
            </div>
            <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs text-orange-600"
              onClick={() => setAmount(String(bike.amount_pending))} data-testid="payment-full-btn">
              Cobrar todo lo pendiente ({formatCurrency(bike.amount_pending)})
            </Button>
          </div>
          <div className="space-y-2">
            <Label>Fecha del cobro</Label>
            <Input type="date" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} required data-testid="payment-date-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Método (opcional)</Label>
              <Input value={method} onChange={(e) => setMethod(e.target.value)} placeholder="Efectivo, transferencia…" data-testid="payment-method-input" />
            </div>
            <div className="space-y-2">
              <Label>Nota (opcional)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Observaciones" data-testid="payment-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="payment-submit-btn">
              Registrar pago
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PaymentsHistory({ bike, onDeleted }) {
  const remove = async (pid) => {
    if (!window.confirm('¿Eliminar este pago? Se recalculará el importe pendiente.')) return;
    try {
      await axios.delete(`${API}/bicycles/${bike.id}/payments/${pid}`);
      toast.success('Pago eliminado');
      onDeleted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar el pago');
    }
  };
  if (!bike.payments?.length) return <p className="text-sm text-slate-500 italic py-2">Sin pagos registrados</p>;
  return (
    <div className="space-y-1 py-2" data-testid={`payments-history-${bike.id}`}>
      {bike.payments.map((p, idx) => (
        <div key={p.id} className="flex items-center justify-between text-sm bg-white border border-slate-200 rounded px-3 py-1.5" data-testid={`payment-item-${p.id}`}>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 w-14">Cobro {idx + 1}</span>
            <span className="font-semibold text-green-700">{formatCurrency(p.amount)}</span>
            <span className="text-slate-500">{formatDate(p.date)}</span>
            {p.method && <Badge variant="outline" className="text-xs">{p.method}</Badge>}
            {p.note && <span className="text-slate-500 text-xs">{p.note}</span>}
            {p.user && <span className="text-slate-400 text-xs">· registrado por {p.user}</span>}
          </div>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" onClick={() => remove(p.id)} data-testid={`delete-payment-${p.id}`}>
            <Trash2 size={14} />
          </Button>
        </div>
      ))}
    </div>
  );
}

export default function SoldPendingCollection() {
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCollected, setShowCollected] = useState(false);
  const [payingBike, setPayingBike] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const fetchBikes = async () => {
    try {
      const res = await axios.get(`${API}/bicycles/sold/pending-collection`, { params: { include_collected: showCollected } });
      setBikes(res.data);
    } catch {
      toast.error('Error al cargar las ventas pendientes de cobro');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBikes(); }, [showCollected]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => {
    const pend = bikes.filter((b) => b.payment_status === 'pendiente');
    return {
      count: pend.length,
      sold: pend.reduce((s, b) => s + (b.sold_price || 0), 0),
      paid: pend.reduce((s, b) => s + (b.amount_paid || 0), 0),
      pending: pend.reduce((s, b) => s + (b.amount_pending || 0), 0),
    };
  }, [bikes]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sold-pending-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Barlow_Condensed'] text-slate-800 flex items-center gap-2">
            <HandCoins className="text-orange-500" /> Vendidas pendiente de cobro
          </h1>
          <p className="text-slate-500 text-sm">Toda venta con importe pendiente aparece aquí automáticamente hasta quedar cobrada al 100 %</p>
        </div>
        <Button variant={showCollected ? 'default' : 'outline'} size="sm" onClick={() => setShowCollected(!showCollected)} data-testid="toggle-collected-btn">
          {showCollected ? 'Ocultar cobradas' : 'Ver también cobradas'}
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200"><CardContent className="p-4">
          <p className="text-xs font-semibold uppercase text-slate-500">Pendientes</p>
          <p className="text-2xl font-bold font-['Barlow_Condensed'] text-slate-800" data-testid="stat-pending-count">{totals.count}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <p className="text-xs font-semibold uppercase text-slate-500">Total ventas</p>
          <p className="text-2xl font-bold font-['Barlow_Condensed'] text-slate-800">{formatCurrency(totals.sold)}</p>
        </CardContent></Card>
        <Card className="border-green-200 bg-green-50"><CardContent className="p-4">
          <p className="text-xs font-semibold uppercase text-green-600">Cobrado</p>
          <p className="text-2xl font-bold font-['Barlow_Condensed'] text-green-700">{formatCurrency(totals.paid)}</p>
        </CardContent></Card>
        <Card className="border-orange-200 bg-orange-50"><CardContent className="p-4">
          <p className="text-xs font-semibold uppercase text-orange-600">Pendiente de cobro</p>
          <p className="text-2xl font-bold font-['Barlow_Condensed'] text-orange-700" data-testid="stat-pending-amount">{formatCurrency(totals.pending)}</p>
        </CardContent></Card>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Cargando…</div>
          ) : bikes.length === 0 ? (
            <div className="p-12 text-center text-slate-500" data-testid="empty-state">
              <CheckCircle2 className="mx-auto mb-2 text-green-500" size={32} />
              No hay bicicletas vendidas pendientes de cobro
            </div>
          ) : (
            <Table data-testid="pending-collection-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-bold uppercase text-slate-500">Bicicleta</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-slate-500">Cliente</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-slate-500">F. Venta</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-slate-500 text-right">P. Venta</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-slate-500 text-right">Cobrado</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-slate-500 text-right">Pendiente</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-slate-500">Estado</TableHead>
                  <TableHead className="text-xs font-bold uppercase text-slate-500 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bikes.map((b) => (
                  <Fragment key={b.id}>
                    <TableRow className="hover:bg-slate-50" data-testid={`pending-row-${b.id}`}>
                      <TableCell>
                        <div className="font-semibold text-slate-800">#{b.number} {b.is_electric && <Badge className="ml-1 bg-orange-100 text-orange-700 border-orange-300 text-[10px]">eBike</Badge>}</div>
                        <div className="text-xs text-slate-500">{b.model} · {b.size}</div>
                      </TableCell>
                      <TableCell className="text-slate-700">{b.sold_to || <span className="italic text-slate-400">Sin cliente</span>}</TableCell>
                      <TableCell className="text-slate-600">{formatDate(b.sold_date)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(b.sold_price)}</TableCell>
                      <TableCell className="text-right text-green-700 font-medium">{formatCurrency(b.amount_paid)}</TableCell>
                      <TableCell className="text-right text-orange-700 font-bold" data-testid={`pending-amount-${b.id}`}>{formatCurrency(b.amount_pending)}</TableCell>
                      <TableCell>
                        {b.payment_status === 'cobrada' ? (
                          <Badge className="bg-green-100 text-green-700 border-green-300 gap-1"><CheckCircle2 size={12} /> Cobrada</Badge>
                        ) : (
                          <Badge className="bg-orange-100 text-orange-700 border-orange-300 gap-1"><Clock size={12} /> Pendiente</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {b.payment_status === 'pendiente' && (
                            <Button size="sm" className="h-8 bg-green-600 hover:bg-green-700 text-white gap-1" onClick={() => setPayingBike(b)} data-testid={`add-payment-btn-${b.id}`}>
                              <Euro size={14} /> Cobrar
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="h-8 px-2 gap-1 text-xs" onClick={() => setExpanded(expanded === b.id ? null : b.id)} data-testid={`toggle-history-${b.id}`}>
                            {b.payments?.length || 0} cobro{(b.payments?.length || 0) === 1 ? '' : 's'}
                            {expanded === b.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </Button>
                          <Link to={`/bicycles/${b.id}`}>
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0"><Eye size={16} /></Button>
                          </Link>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expanded === b.id && (
                      <TableRow className="bg-slate-50/60">
                        <TableCell colSpan={8} className="px-6">
                          <div className="flex items-center justify-between pt-2">
                            <p className="text-xs font-semibold uppercase text-slate-500">Historial de cobros</p>
                            <p className="text-xs text-slate-500">Vendido {formatCurrency(b.sold_price)} · Cobrado <span className="text-green-700 font-semibold">{formatCurrency(b.amount_paid)}</span> · Pendiente <span className="text-orange-700 font-semibold">{formatCurrency(b.amount_pending)}</span></p>
                          </div>
                          <PaymentsHistory bike={b} onDeleted={fetchBikes} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PaymentDialog bike={payingBike} onClose={() => setPayingBike(null)} onSaved={fetchBikes} />
    </div>
  );
}
